import axios, { AxiosError, AxiosRequestConfig, AxiosRequestHeaders, Method } from 'axios'
import { z } from 'zod'
import useStore from '../store'

// Shape 1: Moleculer ValidationError — discriminant: name === 'ValidationError'
// .message is human-readable; .data holds field-level details (direct array or { data: [...] })
const ValidationDetailSchema = z.object({ message: z.string() })
const ValidationDetailsSchema = z
  .union([z.array(ValidationDetailSchema), z.object({ data: z.array(ValidationDetailSchema) })])
  .transform(val => (Array.isArray(val) ? val : val.data))

const ValidationErrorSchema = z.object({
  name: z.literal('ValidationError'),
  message: z.string().optional(),
  data: z.unknown().optional(),
})

// Shape 2: Moleculer-web HTTP errors — discriminant: .type exists
// Constructor is (type, data), so .message is a hardcoded generic string
// and the developer's human reason ends up in .type
const TypedErrorSchema = z.object({
  type: z.string(),
})

// Shape 3: Generic errors — plain string throws wrapped by Moleculer, native Error, etc.
// .message is the best available
const MessageErrorSchema = z.object({
  message: z.string().optional(),
})

const baseURLHostname = process.env.API_HOSTNAME
  ? process.env.API_HOSTNAME
  : process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000'
    : ''
const currentClientAppVersion = process.env.APP_VERSION

const axiosApi = axios.create({
  // If NODE_ENV is not set or set to something else than 'development'
  // We then consider to be in production
  baseURL: baseURLHostname ? new URL('/api/', baseURLHostname).href : '/api',
})

axiosApi.interceptors.response.use(
  res => res.data,
  err => {
    if (axios.isAxiosError(err)) {
      const error = err.toJSON()
      // If the server tells the client that its app version is unsupported
      // Server sends a 406 Not Acceptable
      // @ts-expect-error
      if (error.status === 406) {
        useStore.getState().app.setAppNeedsUpdate(true)
      }
    }
    return Promise.reject(err)
  },
)

axiosApi.interceptors.request.use(req => {
  if (useStore.getState().app.appNeedsUpdate) {
    // If app needs update, we cancel subsequent requests until the browser is (force) refreshed
    throw new axios.Cancel('Version Not Supported')
  }
  return req
})

interface ApiCallOptions<
  Params = {
    [key: string]: any
  },
> {
  endpoint?: string
  params?: Params
  method: Method
  headers?: AxiosRequestHeaders
}

export interface ApiErrorData {
  code: number
  data: { actual: string; field: string; message: string; type: string }[]
  message?: string
  name: string
  type: string
}

export type ApiError = AxiosError<ApiErrorData>

export type ApiErrorExtracted = { message: string; details: string[]; code: number | undefined }

export const extractApiError = (error: ApiError): ApiErrorExtracted => {
  const body = error.response?.data
  const code = error.response?.status

  const validationParsed = ValidationErrorSchema.safeParse(body)
  if (validationParsed.success) {
    const detailsParsed = ValidationDetailsSchema.safeParse(validationParsed.data.data)
    return {
      code,
      message: validationParsed.data.message ?? 'An unexpected error occurred',
      details: detailsParsed.success ? detailsParsed.data.map(d => d.message) : [],
    }
  }

  const typedParsed = TypedErrorSchema.safeParse(body)
  if (typedParsed.success) {
    return { code, message: typedParsed.data.type, details: [] }
  }

  const messageParsed = MessageErrorSchema.safeParse(body)
  return {
    code,
    message: messageParsed.success
      ? (messageParsed.data.message ?? 'An unexpected error occurred')
      : 'An unexpected error occurred',
    details: [],
  }
}

const api = async <Data, Params>({
  endpoint,
  params,
  method,
  headers,
}: ApiCallOptions<Params>): Promise<Data> => {
  if (!headers) headers = {}

  const { session } = useStore.getState()
  headers.role = session.currentUserRole
  if (session.jwt) headers.authorization = session.jwt
  if (session.currentOrganization?._id)
    headers.organization = session.currentOrganization._id.toString()

  // We set the current app version as header for all outgoing requests
  if (currentClientAppVersion) headers['x-app-version'] = currentClientAppVersion

  const config: AxiosRequestConfig = {
    method: method || 'get',
    url: endpoint,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  }

  if (params && method.toLocaleLowerCase() === 'get') config.params = params
  else config.data = params

  return await axiosApi.request(config)
}

export default api
